import React, { useState, createContext, useContext } from 'react';
import { useTranslation } from '../utils/translations';

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('english');
  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const t = useTranslation(language);
  
  const languages = {
    english: { name: 'English', flag: '🇺🇸' },
    hindi: { name: 'हिंदी', flag: '🇮🇳' },
    tamil: { name: 'தமிழ்', flag: '🇮🇳' },
    telugu: { name: 'తెలుగు', flag: '🇮🇳' },
    gujarati: { name: 'ગુજરાતી', flag: '🇮🇳' },
    kannada: { name: 'ಕನ್ನಡ', flag: '🇮🇳' }
  };

  return (
    <header className="bg-white shadow-soft sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-dark-azure">{t('appName')}</h1>
          </div>
          
          <nav className="hidden md:flex space-x-8">
            <a href="#dashboard" className="text-gray-700 hover:text-dark-azure transition-colors duration-300 font-medium">{t('dashboard')}</a>
            <a href="#prediction" className="text-gray-700 hover:text-dark-azure transition-colors duration-300 font-medium">{t('cropPrediction')}</a>
            <a href="#disease" className="text-gray-700 hover:text-dark-azure transition-colors duration-300 font-medium">{t('diseaseCheck')}</a>
            <a href="#weather" className="text-gray-700 hover:text-dark-azure transition-colors duration-300 font-medium">{t('weather')}</a>
          </nav>
          
          <div className="hidden md:flex items-center space-x-4">
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-all duration-300 shadow-lg"
              >
                <span className="text-lg">{languages[language].flag}</span>
                <span>{languages[language].name}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-strong border z-50">
                  {Object.entries(languages).map(([key, lang]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setLanguage(key);
                        setIsLangOpen(false);
                      }}
                      className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-200 first:rounded-t-xl last:rounded-b-xl ${
                        language === key ? 'bg-blue-50 text-dark-azure font-medium' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button 
              onClick={() => window.location.href = '/my-farm'}
              className="btn-primary"
            >
              {t('myFarm')}
            </button>
          </div>
          
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-dark-azure p-2"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        
        {isMenuOpen && (
          <div className="md:hidden pb-4">
            <div className="flex flex-col space-y-3">
              <a href="#dashboard" className="text-gray-700 hover:text-dark-azure transition-colors duration-300 font-medium">{t('dashboard')}</a>
              <a href="#prediction" className="text-gray-700 hover:text-dark-azure transition-colors duration-300 font-medium">{t('cropPrediction')}</a>
              <a href="#disease" className="text-gray-700 hover:text-dark-azure transition-colors duration-300 font-medium">{t('diseaseCheck')}</a>
              <a href="#weather" className="text-gray-700 hover:text-dark-azure transition-colors duration-300 font-medium">{t('weather')}</a>
              <div className="mt-4 space-y-3">
                <div className="relative">
                  <button
                    onClick={() => setIsLangOpen(!isLangOpen)}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl"
                  >
                    <span className="text-lg">{languages[language].flag}</span>
                    <span>{languages[language].name}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {isLangOpen && (
                    <div className="absolute left-0 mt-2 w-full bg-white rounded-xl shadow-strong border z-50">
                      {Object.entries(languages).map(([key, lang]) => (
                        <button
                          key={key}
                          onClick={() => {
                            setLanguage(key);
                            setIsLangOpen(false);
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-200 first:rounded-t-xl last:rounded-b-xl ${
                            language === key ? 'bg-blue-50 text-dark-azure font-medium' : 'text-gray-700'
                          }`}
                        >
                          <span className="text-lg">{lang.flag}</span>
                          <span>{lang.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => window.location.href = '/my-farm'}
                  className="btn-primary w-full"
                >
                  {t('myFarm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;